import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { 
  Brain, Plus, History, FileText, Link as LinkIcon,
  AlertCircle, CheckCircle, Mail, Calendar, Copy, Trash2,
  Download, Lightbulb, Zap, Target, Clock, Play, Pause, Search
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { 
  RadarChart, 
  PolarGrid, 
  PolarAngleAxis, 
  PolarRadiusAxis, 
  Radar, 
  ResponsiveContainer,
  Legend
} from 'recharts';

// Normalizes text for search: lowercase and strips accents/special characters
const normalizeSearchText = (text) => {
  return (text || '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, '')
    .trim();
};

const CognitiveAssessmentPage = () => {
  const { user } = useAuth();
  const ALL_TABS = ['new', 'description', 'history', 'reports', 'public-links'];
  const allowedTabs = ALL_TABS;
  const { toast } = useToast();
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
  
  const [activeTab, setActiveTab] = useState(allowedTabs.includes('new') ? 'new' : (allowedTabs[0] || 'new'));
  const [loading, setLoading] = useState(false);
  
  // Questions and assessment state
  const [questions, setQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [assessmentStarted, setAssessmentStarted] = useState(false);
  
  // Timer state
  const [timerEnabled, setTimerEnabled] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(60 * 60); // 60 minutes in seconds
  const [timerRunning, setTimerRunning] = useState(false);
  
  // Form state
  const [participantName, setParticipantName] = useState('');
  const [participantEmail, setParticipantEmail] = useState('');
  const [selectedEmployeeId, setSelectedEmployeeId] = useState('');
  
  // Employees
  const [employees, setEmployees] = useState([]);
  
  // Assessment results
  const [assessments, setAssessments] = useState([]);
  const [selectedAssessment, setSelectedAssessment] = useState(null);
  const [report, setReport] = useState(null);

  // History filters
  const [historyNameFilter, setHistoryNameFilter] = useState('');
  const [historyDateFilter, setHistoryDateFilter] = useState('');
  const filteredHistoryAssessments = assessments.filter(assessment => {
    const nameMatch = !historyNameFilter || normalizeSearchText(assessment.participant_name).includes(normalizeSearchText(historyNameFilter));
    const dateMatch = !historyDateFilter || new Date(assessment.assessment_date).toISOString().slice(0, 10) === historyDateFilter;
    return nameMatch && dateMatch;
  });
  
  // Public links
  const [publicLinks, setPublicLinks] = useState([]);
  const [showLinkForm, setShowLinkForm] = useState(false);
  const [linkParticipantName, setLinkParticipantName] = useState('');
  const [linkParticipantEmail, setLinkParticipantEmail] = useState('');
  const [expirationDays, setExpirationDays] = useState(7);

  const questionsPerPage = 10;
  const totalPages = Math.ceil(60 / questionsPerPage);
  const currentPage = Math.floor(currentQuestionIndex / questionsPerPage);

  useEffect(() => {
    fetchEmployees();
    fetchAssessments();
    fetchPublicLinks();
  }, []);

  useEffect(() => {
    let interval;
    if (timerRunning && timeRemaining > 0) {
      interval = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            setTimerRunning(false);
            handleSubmitAssessment();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [timerRunning, timeRemaining]);

  const fetchEmployees = async () => {
    try {
      const response = await axios.get(`${backendUrl}/api/employees?active_only=true`);
      setEmployees(response.data);
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  const fetchAssessments = async () => {
    try {
      const response = await axios.get(`${backendUrl}/api/cognitive/assessments`);
      setAssessments(response.data);
    } catch (error) {
      console.error('Error fetching assessments:', error);
    }
  };

  const fetchPublicLinks = async () => {
    try {
      const response = await axios.get(`${backendUrl}/api/cognitive/public-links`);
      setPublicLinks(response.data || []);
    } catch (error) {
      console.error('Error fetching public links:', error);
    }
  };

  const startNewAssessment = async () => {
    if (participantEmail.trim()) {
      try {
        setLoading(true);
        const response = await axios.post(`${backendUrl}/api/cognitive/assessment/start`, {
          participant_name: participantName.trim(),
          participant_email: participantEmail.trim()
        });
        if (response.data.email_sent) {
          toast({
            title: "Link enviado!",
            description: `Link da avaliação enviado com sucesso para ${participantEmail}`,
          });
        } else {
          toast({
            title: "Link criado",
            description: `Link criado, mas não foi possível enviar o email para ${participantEmail}. Copie o link manualmente na aba Links Públicos.`,
            variant: "destructive"
          });
        }
        setAssessmentStarted(false);
      } catch (error) {
        console.error('Error creating assessment link:', error);
        toast({
          title: "Erro",
          description: "Não foi possível criar o link da avaliação",
          variant: "destructive"
        });
      } finally {
        setLoading(false);
      }
      return;
    }

    try {
      setLoading(true);
      const response = await axios.get(`${backendUrl}/api/cognitive/questions`);
      setQuestions(response.data.questions);
      setAssessmentStarted(true);
      setCurrentQuestionIndex(0);
      setAnswers({});
      
      if (timerEnabled) {
        setTimerRunning(true);
      }
      
      toast({
        title: "Avaliação Iniciada",
        description: "Responda todas as 60 questões. Boa sorte!",
      });
    } catch (error) {
      console.error('Error starting assessment:', error);
      toast({
        title: "Erro",
        description: "Não foi possível carregar as questões.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAnswerSelect = (questionId, option) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: option
    }));
  };

  const handleNext = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
    }
  };

  const handleSubmitAssessment = async () => {
    try {
      setLoading(true);
      setTimerRunning(false);
      
      const answersArray = Object.keys(answers).map(questionId => ({
        question_id: parseInt(questionId),
        selected_option: answers[questionId]
      }));
      
      const totalTimeMinutes = timerEnabled ? Math.ceil((60 * 60 - timeRemaining) / 60) : null;
      
      const response = await axios.post(`${backendUrl}/api/cognitive/assessment`, {
        participant_name: participantName,
        participant_email: participantEmail,
        employee_id: selectedEmployeeId || null,
        answers: answersArray,
        time_minutes: totalTimeMinutes
      });
      
      toast({
        title: "Sucesso!",
        description: `Avaliação concluída. ICG: ${response.data.icg} (${response.data.classificacao})`,
      });
      
      setAssessmentStarted(false);
      setActiveTab('history');
      await fetchAssessments();
      
    } catch (error) {
      console.error('Error submitting assessment:', error);
      toast({
        title: "Erro",
        description: "Não foi possível salvar a avaliação.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const viewReport = async (assessmentId) => {
    try {
      setLoading(true);
      const response = await axios.get(`${backendUrl}/api/cognitive/assessment/${assessmentId}/report`);
      setReport(response.data);
      setActiveTab('reports');
    } catch (error) {
      console.error('Error fetching report:', error);
      toast({
        title: "Erro",
        description: "Não foi possível carregar o relatório.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const downloadPDF = async (assessmentId) => {
    try {
      setLoading(true);
      
      const response = await axios.get(
        `${backendUrl}/api/cognitive/assessment/${assessmentId}/pdf`,
        { responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      const assessment = assessments.find(a => a.id === assessmentId);
      const filename = `Cognitivo_${assessment?.participant_name || 'Relatorio'}_${new Date().toISOString().split('T')[0]}.pdf`;
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast({
        title: "Sucesso",
        description: "Relatório PDF baixado com sucesso!",
      });
      
    } catch (error) {
      console.error('Erro ao baixar PDF:', error);
      toast({
        title: "Erro",
        description: "Não foi possível baixar o relatório PDF",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const deleteAssessment = async (assessmentId) => {
    // Confirmar antes de deletar
    if (!window.confirm('Tem certeza que deseja excluir esta avaliação? Esta ação não pode ser desfeita.')) {
      return;
    }

    try {
      setLoading(true);
      
      await axios.delete(`${backendUrl}/api/cognitive/assessment/${assessmentId}`);
      
      // Atualizar lista removendo a avaliação deletada
      setAssessments(prevAssessments => 
        prevAssessments.filter(assessment => assessment.id !== assessmentId)
      );
      
      toast({
        title: "Sucesso",
        description: "Avaliação excluída com sucesso!",
      });
      
    } catch (error) {
      console.error('Erro ao deletar avaliação:', error);
      toast({
        title: "Erro",
        description: error.response?.data?.detail || "Não foi possível excluir a avaliação",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const createPublicLink = async () => {
    try {
      setLoading(true);
      
      const response = await axios.post(`${backendUrl}/api/cognitive/public-link`, {
        participant_name: linkParticipantName,
        participant_email: linkParticipantEmail,
        expiration_days: expirationDays
      });
      
      const fullUrl = `${window.location.origin}/cognitive/public/${response.data.token}`;
      
      await fetchPublicLinks();
      
      setShowLinkForm(false);
      setLinkParticipantName('');
      setLinkParticipantEmail('');
      
      toast({
        title: "Link Criado!",
        description: "Link público criado com sucesso.",
      });
      
    } catch (error) {
      console.error('Error creating public link:', error);
      toast({
        title: "Erro",
        description: "Não foi possível criar o link público.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (token) => {
    const url = `${window.location.origin}/cognitive/public/${token}`;
    navigator.clipboard.writeText(url);
    toast({
      title: "Copiado!",
      description: "Link copiado para a área de transferência.",
    });
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const getICGColor = (icg) => {
    if (icg >= 125) return 'text-purple-600';
    if (icg >= 105) return 'text-blue-600';
    if (icg >= 89) return 'text-green-600';
    if (icg >= 73) return 'text-yellow-600';
    return 'text-red-600';
  };

  const currentQuestions = questions.slice(
    currentPage * questionsPerPage,
    (currentPage + 1) * questionsPerPage
  );

  const progress = ((currentQuestionIndex + 1) / questions.length) * 100;

  return (
    <div className="container mx-auto p-6 max-w-7xl">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-3">
          <Brain className="h-8 w-8 text-purple-600" />
          Avaliação de Capacidades Cognitivas GridProfile
        </h1>
        <p className="text-gray-600 mt-2">
          Sistema de avaliação de raciocínio lógico, numérico e abstrato
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-5">
          {allowedTabs.includes('new') && <TabsTrigger value="new" className="flex items-center gap-2">
            <Plus className="h-4 w-4" />
            Nova Avaliação
          </TabsTrigger>}
          {allowedTabs.includes('description') && <TabsTrigger value="description" className="flex items-center gap-2">
            <Lightbulb className="h-4 w-4" />
            Descrição
          </TabsTrigger>}
          {allowedTabs.includes('history') && <TabsTrigger value="history" className="flex items-center gap-2">
            <History className="h-4 w-4" />
            Histórico
          </TabsTrigger>}
          {allowedTabs.includes('reports') && <TabsTrigger value="reports" className="flex items-center gap-2">
            <FileText className="h-4 w-4" />
            Relatórios
          </TabsTrigger>}
          {allowedTabs.includes('public-links') && <TabsTrigger value="public-links" className="flex items-center gap-2">
            <LinkIcon className="h-4 w-4" />
            Links Públicos
          </TabsTrigger>}
        </TabsList>

        {/* Nova Avaliação Tab */}
        {allowedTabs.includes('new') && <TabsContent value="new" className="space-y-6">
          {!assessmentStarted ? (
            <Card>
              <CardHeader>
                <CardTitle>Iniciar Nova Avaliação Cognitiva</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Nome do Participante *</label>
                  <input
                    type="text"
                    value={participantName}
                    onChange={(e) => setParticipantName(e.target.value.replace(/\d/g, ''))}
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="Nome completo"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Email (opcional)</label>
                  <input
                    type="email"
                    value={participantEmail}
                    onChange={(e) => setParticipantEmail(e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="email@exemplo.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Vincular o Colaborador (opcional)</label>
                  <select
                    value={selectedEmployeeId}
                    onChange={(e) => setSelectedEmployeeId(e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg"
                  >
                    <option value="">Nenhum</option>
                    {employees.map(emp => (
                      <option key={emp.id} value={emp.id}>{emp.nome}</option>
                    ))}
                  </select>
                </div>

                <div className="border-t pt-4">
                  <div className="flex items-center gap-3 mb-4">
                    <input
                      type="checkbox"
                      id="timer"
                      checked={timerEnabled}
                      onChange={(e) => setTimerEnabled(e.target.checked)}
                      className="h-4 w-4"
                    />
                    <label htmlFor="timer" className="text-sm font-medium">
                      Habilitar Timer (60 minutos)
                    </label>
                  </div>
                </div>

                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                  <h3 className="font-semibold text-purple-900 mb-2">ℹ️ Informações da Avaliação</h3>
                  <ul className="text-sm text-purple-800 space-y-1">
                    <li>• Total: 60 questões</li>
                    <li>• 5 categorias: Raciocínio Lógico, Numérico, Abstrato, Resolução de Problemas, Velocidade</li>
                    <li>• Tempo estimado: 45-60 minutos</li>
                    <li>• Resultado: ICG (Índice Cognitivo GridProfile) de 55 a 145</li>
                  </ul>
                </div>

                <Button
                  onClick={startNewAssessment}
                  disabled={!participantName || loading}
                  className="w-full bg-purple-600 hover:bg-purple-700"
                >
                  <Play className="h-4 w-4 mr-2" />
                  Iniciar Avaliação
                </Button>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle>Questão {currentQuestionIndex + 1} de {questions.length}</CardTitle>
                  {timerEnabled && (
                    <div className="flex items-center gap-2">
                      <Clock className="h-5 w-5 text-purple-600" />
                      <span className={`text-lg font-mono font-bold ${
                        timeRemaining < 300 ? 'text-red-600' : 'text-purple-600'
                      }`}>
                        {formatTime(timeRemaining)}
                      </span>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setTimerRunning(!timerRunning)}
                      >
                        {timerRunning ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                      </Button>
                    </div>
                  )}
                </div>
                <div className="mt-4">
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-purple-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {currentQuestions.map((question, idx) => {
                  const globalIndex = currentPage * questionsPerPage + idx;
                  if (globalIndex !== currentQuestionIndex) return null;
                  
                  return (
                    <div key={question.id} className="space-y-4">
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <p className="text-lg font-medium text-gray-800">
                          {question.question}
                        </p>
                      </div>

                      <div className="space-y-2">
                        {Object.entries(question.options).map(([key, value]) => (
                          <button
                            key={key}
                            onClick={() => handleAnswerSelect(question.id, key)}
                            className={`w-full p-4 text-left rounded-lg border-2 transition-all ${
                              answers[question.id] === key
                                ? 'border-purple-600 bg-purple-50'
                                : 'border-gray-200 hover:border-purple-300'
                            }`}
                          >
                            <span className="font-semibold text-purple-600 mr-3">{key.toUpperCase()})</span>
                            {value}
                          </button>
                        ))}
                      </div>

                      <div className="flex justify-between pt-4">
                        <Button
                          onClick={handlePrevious}
                          disabled={currentQuestionIndex === 0}
                          variant="outline"
                        >
                          ← Anterior
                        </Button>

                        {currentQuestionIndex < questions.length - 1 ? (
                          <Button
                            onClick={handleNext}
                            className="bg-purple-600 hover:bg-purple-700"
                          >
                            Próxima →
                          </Button>
                        ) : (
                          <Button
                            onClick={handleSubmitAssessment}
                            disabled={Object.keys(answers).length < questions.length || loading}
                            className="bg-green-600 hover:bg-green-700"
                          >
                            {loading ? 'Enviando...' : 'Concluir Avaliação'}
                          </Button>
                        )}
                      </div>

                      {Object.keys(answers).length < questions.length && currentQuestionIndex === questions.length - 1 && (
                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-center">
                          <AlertCircle className="inline h-4 w-4 mr-2" />
                          <span className="text-sm text-yellow-800">
                            Responda todas as questões antes de concluir ({Object.keys(answers).length}/{questions.length} respondidas)
                          </span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          )}
        </TabsContent>}

        {/* Description Tab */}
        {allowedTabs.includes('description') && <TabsContent value="description" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-2xl font-bold flex items-center gap-2">
                <Lightbulb className="h-6 w-6 text-purple-600" />
                Sobre a Avaliação Cognitiva GridProfile
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="bg-purple-50 border-l-4 border-purple-500 p-6 rounded-r-lg">
                <h3 className="text-xl font-bold text-purple-900 mb-3">📋 Metodologia</h3>
                <div className="space-y-3 text-gray-700">
                  <p>
                    A <strong>Avaliação de Capacidades Cognitivas GridProfile</strong> é uma ferramenta proprietária 
                    desenvolvida para avaliar habilidades cognitivas em processos seletivos e desenvolvimento profissional.
                  </p>
                  <p>
                    <strong>Importante:</strong> Esta é uma ferramenta de triagem cognitiva, NÃO um teste psicológico validado. 
                    Útil para processos seletivos, mas não substitui avaliação psicológica formal.
                  </p>
                  <p>
                    A avaliação mede <strong>5 capacidades cognitivas</strong> através de <strong>60 questões</strong> 
                    (12 por categoria), gerando um <strong>Índice Cognitivo GridProfile (ICG)</strong> na escala de 55 a 145.
                  </p>
                </div>
              </div>

              <div>
                <h3 className="text-2xl font-bold text-gray-800 mb-4">As 5 Capacidades Avaliadas</h3>
                
                <div className="space-y-4">
                  <Card className="border-2 border-blue-200">
                    <CardContent className="p-6">
                      <div className="flex items-start gap-4">
                        <Target className="h-8 w-8 text-blue-600 mt-1" />
                        <div>
                          <h4 className="text-xl font-bold text-blue-900 mb-2">1. Raciocínio Lógico</h4>
                          <p className="text-gray-700">
                            Avalia a capacidade de pensar logicamente, fazer deduções e estabelecer relações entre conceitos. 
                            Inclui silogismos, analogias e problemas de lógica proposicional.
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="border-2 border-green-200">
                    <CardContent className="p-6">
                      <div className="flex items-start gap-4">
                        <Zap className="h-8 w-8 text-green-600 mt-1" />
                        <div>
                          <h4 className="text-xl font-bold text-green-900 mb-2">2. Raciocínio Numérico</h4>
                          <p className="text-gray-700">
                            Mede a habilidade de trabalhar com números, identificar padrões numéricos e resolver problemas matemáticos. 
                            Inclui sequências, progressões e cálculos.
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="border-2 border-purple-200">
                    <CardContent className="p-6">
                      <div className="flex items-start gap-4">
                        <Brain className="h-8 w-8 text-purple-600 mt-1" />
                        <div>
                          <h4 className="text-xl font-bold text-purple-900 mb-2">3. Raciocínio Abstrato</h4>
                          <p className="text-gray-700">
                            Avalia a capacidade de identificar padrões, relações espaciais e pensar de forma abstrata. 
                            Inclui rotações mentais, simetrias e relações geométricas.
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="border-2 border-orange-200">
                    <CardContent className="p-6">
                      <div className="flex items-start gap-4">
                        <Lightbulb className="h-8 w-8 text-orange-600 mt-1" />
                        <div>
                          <h4 className="text-xl font-bold text-orange-900 mb-2">4. Resolução de Problemas</h4>
                          <p className="text-gray-700">
                            Mede a habilidade de aplicar raciocínio lógico e matemático para resolver problemas práticos. 
                            Inclui situações reais, otimização e tomada de decisão.
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="border-2 border-pink-200">
                    <CardContent className="p-6">
                      <div className="flex items-start gap-4">
                        <Zap className="h-8 w-8 text-pink-600 mt-1" />
                        <div>
                          <h4 className="text-xl font-bold text-pink-900 mb-2">5. Velocidade de Processamento</h4>
                          <p className="text-gray-700">
                            Avalia a rapidez no processamento de informações simples. Inclui cálculos básicos, 
                            reconhecimento de padrões e questões que exigem resposta rápida.
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>

              <div className="bg-gradient-to-r from-purple-50 to-blue-50 border-2 border-purple-200 p-6 rounded-lg">
                <h3 className="text-lg font-bold text-gray-800 mb-3">
                  Índice Cognitivo GridProfile (ICG)
                </h3>
                <div className="space-y-2 text-gray-700">
                  <p>O ICG é calculado com base no desempenho geral nas 60 questões:</p>
                  <ul className="list-disc list-inside ml-4 space-y-1">
                    <li><strong>55-72:</strong> Muito Baixo</li>
                    <li><strong>73-88:</strong> Baixo</li>
                    <li><strong>89-104:</strong> Médio (faixa da maioria da população)</li>
                    <li><strong>105-124:</strong> Alto</li>
                    <li><strong>125-145:</strong> Muito Alto</li>
                  </ul>
                  <p className="text-sm italic bg-purple-100 p-3 rounded mt-3">
                    💡 <strong>Importante:</strong> Use como ferramenta de triagem, nunca como critério único de avaliação.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>}

        {/* History Tab */}
        {allowedTabs.includes('history') && <TabsContent value="history" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Histórico de Avaliações</CardTitle>
            </CardHeader>
            <CardContent>
              {assessments.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <History className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                  <p>Nenhuma avaliação realizada ainda.</p>
                </div>
              ) : (
                <>
                  <div className="flex flex-col sm:flex-row gap-3 mb-4">
                    <div className="relative flex-1">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <input
                        type="text"
                        value={historyNameFilter}
                        onChange={(e) => setHistoryNameFilter(e.target.value)}
                        placeholder="Buscar por nome do funcionário..."
                        className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <input
                      type="date"
                      value={historyDateFilter}
                      onChange={(e) => setHistoryDateFilter(e.target.value)}
                      className="border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    {(historyNameFilter || historyDateFilter) && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => { setHistoryNameFilter(''); setHistoryDateFilter(''); }}
                      >
                        Limpar Filtros
                      </Button>
                    )}
                  </div>
                  {filteredHistoryAssessments.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <p>Nenhuma avaliação encontrada para os filtros aplicados.</p>
                    </div>
                  ) : (
                <div className="space-y-3">
                  {filteredHistoryAssessments.map(assessment => (
                    <div key={assessment.id} className="border rounded-lg p-4 hover:bg-gray-50">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-semibold text-lg">{assessment.participant_name}</p>
                          <p className="text-sm text-gray-600">
                            {new Date(assessment.assessment_date).toLocaleDateString('pt-BR')}
                          </p>
                          <div className="flex gap-4 mt-2">
                            <span className={`text-2xl font-bold ${getICGColor(assessment.icg)}`}>
                              ICG: {assessment.icg}
                            </span>
                            <span className="text-sm text-gray-600 self-end">
                              {assessment.classificacao}
                            </span>
                          </div>
                          <p className="text-sm text-gray-600 mt-1">
                            Acertos: {assessment.acertos}/{assessment.total_questoes} ({assessment.score_geral.toFixed(1)}%)
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => viewReport(assessment.id)}
                          >
                            <FileText className="h-4 w-4 mr-1" />
                            Relatório
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => downloadPDF(assessment.id)}
                          >
                            <Download className="h-4 w-4 mr-1" />
                            PDF
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => deleteAssessment(assessment.id)}
                            className="border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700"
                          >
                            <Trash2 className="h-4 w-4 mr-1" />
                            Excluir
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>}

        {/* Reports Tab */}
        {allowedTabs.includes('reports') && <TabsContent value="reports" className="space-y-6">
          {report ? (
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle className="text-2xl">{report.participant_name}</CardTitle>
                    <p className="text-sm text-gray-600">
                      Avaliado em {new Date(report.assessment_date).toLocaleDateString('pt-BR')}
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    onClick={() => setReport(null)}
                  >
                    ← Voltar ao Histórico
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="bg-purple-50 border-2 border-purple-200 rounded-lg p-6 text-center">
                  <p className="text-sm text-gray-600 mb-2">Índice Cognitivo GridProfile</p>
                  <p className={`text-6xl font-bold ${getICGColor(report.icg)}`}>
                    {report.icg}
                  </p>
                  <p className="text-xl font-semibold text-gray-700 mt-2">
                    {report.classificacao}
                  </p>
                  <p className="text-sm text-gray-600 mt-2">
                    Score Geral: {report.score_geral.toFixed(1)}%
                  </p>
                </div>

                {/* Radar Chart */}
                <div className="bg-white rounded-lg p-6 border-2 border-gray-100">
                  <h3 className="text-lg font-semibold mb-4 text-center">Perfil Cognitivo</h3>
                  <ResponsiveContainer width="100%" height={400}>
                    <RadarChart data={[
                      { categoria: 'Lógico', valor: report.raciocinio_logico_score },
                      { categoria: 'Numérico', valor: report.raciocinio_numerico_score },
                      { categoria: 'Abstrato', valor: report.raciocinio_abstrato_score },
                      { categoria: 'Problemas', valor: report.resolucao_problemas_score },
                      { categoria: 'Velocidade', valor: report.velocidade_processamento_score }
                    ]}>
                      <PolarGrid stroke="#e5e7eb" />
                      <PolarAngleAxis 
                        dataKey="categoria" 
                        tick={{ fill: '#374151', fontSize: 12, fontWeight: 600 }}
                      />
                      <PolarRadiusAxis 
                        angle={90} 
                        domain={[0, 100]} 
                        tick={{ fill: '#6b7280', fontSize: 10 }}
                      />
                      <Radar
                        name="Score (%)"
                        dataKey="valor"
                        stroke="#7c3aed"
                        fill="#7c3aed"
                        fillOpacity={0.5}
                        strokeWidth={2}
                      />
                      <Legend formatter={() => 'Pontuação (%)'} />
                    </RadarChart>
                  </ResponsiveContainer>
                </div>

                {/* Scores Detalhados */}
                <div>
                  <h3 className="text-lg font-semibold mb-3">Scores por Categoria</h3>
                  <div className="space-y-2">
                    {[
                      { name: 'Raciocínio Lógico', score: report.raciocinio_logico_score },
                      { name: 'Raciocínio Numérico', score: report.raciocinio_numerico_score },
                      { name: 'Raciocínio Abstrato', score: report.raciocinio_abstrato_score },
                      { name: 'Resolução de Problemas', score: report.resolucao_problemas_score },
                      { name: 'Velocidade de Processamento', score: report.velocidade_processamento_score }
                    ].map((item, idx) => (
                      <div key={idx}>
                        <div className="flex justify-between text-sm mb-1">
                          <span>{item.name}</span>
                          <span className="font-semibold">{item.score.toFixed(1)}%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-purple-600 h-2 rounded-full"
                            style={{ width: `${item.score}%` }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Estatísticas */}
                <div>
                  <h3 className="text-lg font-semibold mb-3">Estatísticas</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="text-sm text-gray-600">Total de Questões</p>
                      <p className="text-2xl font-bold">{report.total_questoes}</p>
                    </div>
                    <div className="bg-green-50 p-4 rounded-lg">
                      <p className="text-sm text-gray-600">Acertos</p>
                      <p className="text-2xl font-bold text-green-600">{report.acertos}</p>
                    </div>
                    <div className="bg-red-50 p-4 rounded-lg">
                      <p className="text-sm text-gray-600">Erros</p>
                      <p className="text-2xl font-bold text-red-600">{report.erros}</p>
                    </div>
                    <div className="bg-yellow-50 p-4 rounded-lg">
                      <p className="text-sm text-gray-600">Não Respondidas</p>
                      <p className="text-2xl font-bold text-yellow-600">{report.nao_respondidas}</p>
                    </div>
                  </div>
                </div>

                {report.time_minutes && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <Clock className="inline h-4 w-4 mr-2 text-blue-600" />
                    <span className="text-sm text-blue-800">
                      <strong>Tempo Total:</strong> {report.time_minutes} minutos
                    </span>
                  </div>
                )}
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="p-12 text-center text-gray-500">
                <FileText className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                <p>Selecione uma avaliação no histórico para ver o relatório.</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>}

        {/* Public Links Tab */}
        {allowedTabs.includes('public-links') && <TabsContent value="public-links" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle>Links Públicos</CardTitle>
                <Button
                  onClick={() => setShowLinkForm(!showLinkForm)}
                  className="bg-purple-600 hover:bg-purple-700"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Criar Novo Link
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {showLinkForm && (
                <div className="border rounded-lg p-4 bg-gray-50 space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Nome do Participante</label>
                    <input
                      type="text"
                      value={linkParticipantName}
                      onChange={(e) => setLinkParticipantName(e.target.value.replace(/\d/g, ''))}
                      className="w-full px-3 py-2 border rounded-lg"
                      placeholder="Nome completo"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Email do Participante</label>
                    <input
                      type="email"
                      value={linkParticipantEmail}
                      onChange={(e) => setLinkParticipantEmail(e.target.value)}
                      className="w-full px-3 py-2 border rounded-lg"
                      placeholder="email@exemplo.com"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Validade (dias)</label>
                    <input
                      type="number"
                      value={expirationDays}
                      onChange={(e) => setExpirationDays(parseInt(e.target.value))}
                      className="w-full px-3 py-2 border rounded-lg"
                      min="1"
                      max="90"
                    />
                  </div>

                  <div className="flex gap-2">
                    <Button
                      onClick={createPublicLink}
                      disabled={!linkParticipantName || !linkParticipantEmail || loading}
                      className="bg-purple-600 hover:bg-purple-700"
                    >
                      Criar Link
                    </Button>
                    <Button
                      onClick={() => setShowLinkForm(false)}
                      variant="outline"
                    >
                      Cancelar
                    </Button>
                  </div>
                </div>
              )}

              {publicLinks.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <LinkIcon className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                  <p>Nenhum link público criado ainda.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {publicLinks.map(link => (
                    <div key={link.id} className="border rounded-lg p-4">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="font-semibold">{link.participant_name || link.name}</p>
                            {link.external_origin === 'gridrecruter' && (
                              <Badge
                                variant="outline"
                                className="bg-indigo-50 text-indigo-700 border-indigo-300 text-[10px]"
                                title={link.external_company_id ? `Empresa externa: ${link.external_company_id}` : 'Criado via API externa'}
                                data-testid={`cognitive-link-gridrecruter-badge-${link.id}`}
                              >
                                GridRecruter
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm text-gray-600">{link.participant_email || link.email}</p>
                          <p className="text-xs text-gray-500 mt-1">
                            Criado em: {new Date(link.created_at).toLocaleDateString('pt-BR')}
                          </p>
                          <p className="text-xs text-gray-500">
                            Expira em: {new Date(link.expires_at).toLocaleDateString('pt-BR')}
                          </p>
                          {link.completed && (
                            <span className="inline-flex items-center text-xs bg-green-100 text-green-800 px-2 py-1 rounded mt-2">
                              <CheckCircle className="h-3 w-3 mr-1" />
                              Completado
                            </span>
                          )}
                        </div>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => copyToClipboard(link.token)}
                          >
                            <Copy className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                      <div className="mt-3 p-2 bg-gray-100 rounded text-xs font-mono break-all">
                        {window.location.origin}/cognitive/public/{link.token}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>}
      </Tabs>
    </div>
  );
};

export default CognitiveAssessmentPage;